import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';

import { Box, Button, FormControl, GridLegacy as Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import { operations } from '@luxuryescapes/contract-svc-promo';
import { getRegions } from '@luxuryescapes/lib-regions';

import useQuery from '~/hooks/useQuery';

import { addQuery } from '~/utils/url';

export type OfferMetricsFormData = {
  offerId: string;
  region: string;
  limit: number;
  sortBy: operations['getOfferMetrics']['parameters']['query']['sortBy'];
  sortOrder: operations['getOfferMetrics']['parameters']['query']['sortOrder'];
};

type OfferMetricsFormProps = {
  onSubmit: (data: OfferMetricsFormData) => void;
  isLoading?: boolean;
  defaultValues?: Partial<OfferMetricsFormData>;
};

const defaultFormValues: OfferMetricsFormData = {
  offerId: '',
  region: 'AU',
  limit: 10,
  sortBy: 'updated_at',
  sortOrder: 'desc',
};

export default function OfferMetricsForm({ onSubmit, isLoading = false, defaultValues = {} }: OfferMetricsFormProps) {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();

  // Read URL parameters and merge with defaultValues
  const getUrlParams = useCallback(() => {
    const urlOfferId = query.get('offerId') || '';
    const urlRegion = query.get('region') || defaultFormValues.region;
    const urlLimit = query.get('limit') ? parseInt(query.get('limit')!, 10) : defaultFormValues.limit;
    const urlSortBy = (query.get('sortBy') as OfferMetricsFormData['sortBy']) || defaultFormValues.sortBy;
    const urlSortOrder = (query.get('sortOrder') as OfferMetricsFormData['sortOrder']) || defaultFormValues.sortOrder;

    return {
      offerId: urlOfferId,
      region: urlRegion,
      limit: urlLimit,
      sortBy: urlSortBy,
      sortOrder: urlSortOrder,
    };
  }, [query]);

  // Initial values: URL params take precedence, then defaultValues prop, then defaultFormValues
  const initialValues = useMemo(() => {
    const urlParams = getUrlParams();
    return {
      ...defaultFormValues,
      ...defaultValues,
      ...urlParams,
    };
  }, [defaultValues, getUrlParams]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<OfferMetricsFormData>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const selectedRegion = watch('region');
  const regions = getRegions();

  // Sync form values when URL params change (e.g., on browser back/forward)
  // Skip initial mount to avoid resetting form unnecessarily
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const urlParams = getUrlParams();
    reset({
      ...defaultFormValues,
      ...defaultValues,
      ...urlParams,
    });
  }, [location.search, reset, getUrlParams, defaultValues]);

  // Handle form submission with URL update
  const handleFormSubmit = (data: OfferMetricsFormData) => {
    // Update URL with form values
    const urlParams: Record<string, string> = {};
    if (data.offerId) urlParams.offerId = data.offerId;
    if (data.region) urlParams.region = data.region;
    if (data.limit) urlParams.limit = data.limit.toString();
    if (data.sortBy) urlParams.sortBy = data.sortBy;
    if (data.sortOrder) urlParams.sortOrder = data.sortOrder;

    const newLocation = addQuery(location, urlParams);
    history.push(newLocation);

    // Call the original onSubmit handler
    onSubmit(data);
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            {...register('offerId')}
            label="Offer ID"
            variant="outlined"
            fullWidth
            error={!!errors.offerId}
            helperText={errors.offerId?.message}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="region-select-label">Region</InputLabel>
            <Select
              labelId="region-select-label"
              label="Region"
              value={selectedRegion || 'AU'}
              onChange={(e) => setValue('region', e.target.value)}
              error={!!errors.region}
            >
              {regions.map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button type="submit" variant="outlined" color="primary" disabled={isLoading} fullWidth>
            {isLoading ? 'Loading...' : 'Search Offer Metrics'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
